import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AdminSidebar } from "@/components/admin/AdminSidebar";
import { Button } from "@/components/ui/button";
import { ArrowLeft, PlusCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { MaterialType } from "@/types/materialType";
import { MaterialTypesTable } from "@/components/admin/material-types/MaterialTypesTable";
import { EditMaterialTypeDialog } from "@/components/admin/material-types/EditMaterialTypeDialog";
import { NewMaterialTypeDialog } from "@/components/admin/material-types/NewMaterialTypeDialog";

export default function MaterialTypes() {
  const navigate = useNavigate();
  const [materials, setMaterials] = useState<MaterialType[]>([]);
  const [editingMaterial, setEditingMaterial] = useState<MaterialType | null>(null);
  const [isNewDialogOpen, setIsNewDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    checkAuth();
    fetchMaterials();
  }, [navigate]);

  const checkAuth = async () => {
    const {
      data: {
        session
      }
    } = await supabase.auth.getSession();
    if (!session) {
      navigate("/auth");
      return;
    }
  };

  const fetchMaterials = async () => {
    try {
      const {
        data: {
          session
        }
      } = await supabase.auth.getSession();
      if (!session) return;
      const {
        data,
        error
      } = await supabase.from('cat_material_prefixes').select('*').order('material_type');
      if (error) throw error;
      setMaterials(data || []);
    } catch (error) {
      console.error('Erro ao carregar tipos de material:', error);
      toast.error('Erro ao carregar tipos de material');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = async () => {
    if (!editingMaterial) return;
    try {
      const {
        data: {
          session
        }
      } = await supabase.auth.getSession();
      if (!session) {
        toast.error('Usuário não autenticado');
        return;
      }
      const {
        error
      } = await supabase.from('cat_material_prefixes').update({
        prefix: editingMaterial.prefix,
        material_type: editingMaterial.material_type
      }).eq('id', editingMaterial.id);
      if (error) throw error;
      toast.success('Tipo de material atualizado com sucesso');
      setEditingMaterial(null);
      fetchMaterials();
    } catch (error) {
      console.error('Erro ao atualizar tipo de material:', error);
      toast.error('Erro ao atualizar tipo de material');
    }
  };

  const handleCreate = async (newMaterial: Omit<MaterialType, 'id' | 'created_at'>) => {
    try {
      const {
        data: {
          session
        }
      } = await supabase.auth.getSession();
      if (!session) {
        toast.error('Usuário não autenticado');
        return;
      }
      const {
        error
      } = await supabase.from('cat_material_prefixes').insert([{
        prefix: newMaterial.prefix,
        material_type: newMaterial.material_type
      }]);
      if (error) throw error;
      toast.success('Tipo de material criado com sucesso');
      setIsNewDialogOpen(false);
      fetchMaterials();
    } catch (error) {
      console.error('Erro ao criar tipo de material:', error);
      toast.error('Erro ao criar tipo de material');
    }
  };

  return (
    <div className="flex min-h-screen bg-background">
      <AdminSidebar />
      
      <div className="flex-1 ml-[250px]">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-3xl font-bold">Tipos de Material</h1>
            <div className="flex gap-2">
              <Button 
                variant="ghost" 
                onClick={() => navigate('/')} 
                className="flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Voltar
              </Button>
              <Button 
                onClick={() => setIsNewDialogOpen(true)} 
                className="flex items-center gap-2 bg-blue-700 hover:bg-blue-600"
              >
                <PlusCircle className="w-4 h-4" />
                Novo Tipo
              </Button>
            </div>
          </div>

          <div className="max-w-[1200px]">
            {isLoading ? (
              <div>Carregando...</div>
            ) : (
              <MaterialTypesTable 
                materials={materials} 
                onEdit={setEditingMaterial} 
              />
            )}
          </div>
        </div>

        <EditMaterialTypeDialog 
          material={editingMaterial} 
          onClose={() => setEditingMaterial(null)} 
          onSave={handleEdit} 
          onChange={setEditingMaterial} 
        />

        <NewMaterialTypeDialog 
          open={isNewDialogOpen} 
          onClose={() => setIsNewDialogOpen(false)} 
          onSave={handleCreate} 
        />
      </div>
    </div>
  );
}
